import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";
import { useConfirm } from "../../components/confirm";
import { LookupCombo } from "../../components/LookupCombo";
import "./SettingsDetailPage.css"; // .tc-* chips, segmented control and dialog rules

/**
 * Test Code → the Category chips row and the toolbar's "Set Category" action
 * (prototype v12).
 *
 * Both read ONE endpoint — `GET /api/v1/test-methods/categories`, backed by
 * `AI_TestMethod_Categories_SP` — so the chip list and the picker can never
 * drift apart.
 *
 * A test counts under EVERY category it carries, so the chip counts can add up
 * to more than the number of tests; the prototype says exactly this. Filtering
 * happens SERVER-side (`?category=`), because a test stores several categories
 * joined with "; " in one legacy column and the chip has to match one item of
 * that list — an exact column filter would miss "Metals" inside
 * "Metals; Nutrients", and filtering the current page client-side would silently
 * hide matches on the other pages.
 *
 * Storage is unchanged: the existing `dbo.TestMethod.Category` nvarchar(200),
 * no new column and no datatype change (owner constraints 2026-10-12/13).
 */

export const UNCATEGORIZED = "(none)";

export type CategoryCount = { category: string; testCount: number };

/** Loads the chip data. Exposed as a hook so the chips row and the Set Category
 *  dialog share one fetch and one refresh. */
export function useTestCodeCategories() {
  const [cats, setCats] = useState<CategoryCount[]>([]);
  const [none, setNone] = useState(0);
  const [total, setTotal] = useState(0);
  const [token, setToken] = useState(0);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get("/api/v1/test-methods/categories");
        if (!alive) return;
        setCats(data?.data?.categories ?? []);
        setNone(data?.data?.uncategorized ?? 0);
        setTotal(data?.data?.total ?? 0);
      } catch {
        // A chip row that fails to load must not take the grid down with it:
        // the list still works, it just shows no chips. The failure is already
        // reported by the shared axios error handling.
        if (alive) {
          setCats([]);
          setNone(0);
          setTotal(0);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  return { cats, none, total, reload: useCallback(() => setToken((n) => n + 1), []) };
}

export function TestCodeCategoryChips({
  cats,
  none,
  total,
  value,
  onChange,
}: {
  cats: CategoryCount[];
  none: number;
  total: number;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  if (!cats.length && !none) return null;
  return (
    <div className="tc-chips">
      <span className="tc-chips-lbl">Category:</span>
      <button
        type="button"
        className={`tc-chip${value === null ? " on" : ""}`}
        onClick={() => onChange(null)}
      >
        All <span className="tc-chip-n">{total}</span>
      </button>
      {cats.map((c) => (
        <button
          key={c.category}
          type="button"
          className={`tc-chip${value === c.category ? " on" : ""}`}
          onClick={() => onChange(c.category)}
        >
          {c.category} <span className="tc-chip-n">{c.testCount}</span>
        </button>
      ))}
      {none > 0 && (
        <button
          type="button"
          className={`tc-chip warn${value === UNCATEGORIZED ? " on" : ""}`}
          onClick={() => onChange(UNCATEGORIZED)}
        >
          Uncategorized <span className="tc-chip-n">{none}</span>
        </button>
      )}
    </div>
  );
}

type Mode = "Add" | "Replace" | "Remove";

/** The toolbar "Set Category" dialog: pick categories, choose how to apply them
 *  to every ticked test, confirm, save. */
export function TestCodeSetCategoryDialog({
  open,
  ids,
  cats,
  onClose,
  onDone,
}: {
  open: boolean;
  ids: string[];
  cats: CategoryCount[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<Mode>("Add");
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    if (open) {
      setMode("Add");
      setPicked([]);
    }
  }, [open]);

  /* The prototype's bulk dialog offers only the EXISTING categories —
     ccHTML('set', [], false), canAdd false (Test-Code-v12.html:1011). A brand-new
     category is created by typing it on a test's own Category field, which is
     free text, not from here. */
  const options = useMemo(
    () => [...new Set(cats.map((c) => c.category))].sort((a, b) => a.localeCompare(b)),
    [cats],
  );

  const save = async () => {
    if (mode !== "Replace" && picked.length === 0) {
      notify.warning("Choose at least one category.");
      return;
    }
    const verb = mode === "Add" ? "Add to existing" : mode;
    const ok = await confirm({
      title: "Set Category",
      message: `${verb} on ${ids.length} selected test${ids.length === 1 ? "" : "s"}. Do you want to Save?`,
    });
    if (!ok) return;

    setBusy(true);
    try {
      const { data } = await api.post("/api/v1/test-methods/set-category", {
        ids,
        categories: picked,
        mode,
      });
      const n = data?.data?.changed ?? 0;
      notify.success(
        n === 0
          ? "No change — those tests already match."
          : `Saved successfully. ${n} test${n === 1 ? "" : "s"} updated.`,
      );
      onDone();
      onClose();
    } catch (e: unknown) {
      // The API turns the SP's routine refusals into a 400 carrying the message
      // written for the person who ticked the boxes — notably the Calculation
      // guard. Show that, not a generic failure.
      notify.error(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Could not set the category.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <AppDialog
      open={open}
      title="Set Category"
      width={560}
      onClose={onClose}
      className="dlg-center tc-setcat-dlg"
      footer={
        <>
          <span className="tc-setcat-count">{ids.length} test(s) selected</span>
          <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={busy}>
            Apply
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <div className="tc-setcat">
        {/* Prototype order: Categories FIRST, then Apply as. Side labels at the
            Department width so the dialog reads like the detail form. */}
        <div className="tc-copy-grid tc-setcat-grid">
          <label className="tc-copy-lbl">Categories<span className="req">*</span></label>
          {/* The Sample Registration checked combo (RegistrationForm "Sample
              Matrix"), shared component, same props: multi + "Select…". The
              popup matches the field width because this one lives in a dialog
              narrower than the control's 520px default. */}
          <LookupCombo
            lookup={{ cols: ["Category"], rows: options.map((o) => [o]) }}
            multi
            selectAllRow
            popupMatchTrigger
            values={picked}
            onToggle={(next) => setPicked(next)}
            placeholder="Select…"
            ariaLabel="Categories"
          />

          <label className="tc-copy-lbl">Apply as</label>
          <div className="tc-seg" role="group" aria-label="Apply as">
            {(["Add", "Replace", "Remove"] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                className={`tc-seg-btn${mode === m ? " on" : ""}`}
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
              >
                {m === "Add" ? "Add to existing" : m}
              </button>
            ))}
          </div>
        </div>
      </div>
    </AppDialog>
  );
}
